// People Insights — the redesigned HR statistics page.
//
// Composes the modular sections under `src/components/peopleInsights/`.
// All HR drill state still lives in the URL via `useHrStats`, so views
// remain shareable and back/forward friendly.

import React, { useCallback, useState } from 'react';
import { Alert, Box, Stack } from '@mui/material';

import { useHrStats } from '../hooks/useHrStats';
import { SEARCE_COLORS } from '../theme';

import PeopleInsightsHeader from '../components/peopleInsights/PeopleInsightsHeader';
import KpiStrip from '../components/peopleInsights/KpiStrip';
import GeoSection from '../components/peopleInsights/GeoSection';
import OrgSection from '../components/peopleInsights/OrgSection';
import CompositionSection from '../components/peopleInsights/CompositionSection';
import LifecycleSnapshot from '../components/peopleInsights/LifecycleSnapshot';
import DrillPanel from '../components/peopleInsights/DrillPanel';
import type { DrillContext } from '../components/peopleInsights/drillContextTypes';
import type { HrDimension } from '../types/hrStats';

const HRStatsPage: React.FC = () => {
  const {
    filters,
    employeeStatus,
    summary,
    isSummaryLoading,
    setEmployeeStatus,
    setFilter,
    drillInto,
    removeFilter,
    clearAllFilters,
  } = useHrStats();

  const [drillCtx, setDrillCtx] = useState<DrillContext | null>(null);

  // Apply a filter to the URL. We keep using `drillInto` (which also picks
  // the next sensible group_by), so the underlying breakdown-table state
  // remains consistent if/when it's re-introduced.
  const handleApplyFilter = useCallback(
    (dim: HrDimension, value: string) => {
      drillInto(dim, value);
    },
    [drillInto],
  );

  const openDrill = useCallback((ctx: DrillContext) => setDrillCtx(ctx), []);
  const closeDrill = useCallback(() => setDrillCtx(null), []);

  const hasZeroPopulation =
    !isSummaryLoading && summary != null && summary.total_headcount === 0;

  return (
    <Box
      sx={{
        backgroundColor: SEARCE_COLORS.background,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <PeopleInsightsHeader
        employeeStatus={employeeStatus}
        onChangeStatus={setEmployeeStatus}
        filters={filters}
        onApplyFilter={setFilter}
        onRemoveFilter={removeFilter}
        onClearAll={clearAllFilters}
      />

      <Box sx={{ px: { xs: 2, md: 3 }, py: 3, maxWidth: 1440, mx: 'auto', width: '100%' }}>
        {hasZeroPopulation ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No employees match the current filters. Try removing a chip in the
            header above.
          </Alert>
        ) : null}

        <Stack spacing={2.5}>
          <KpiStrip
            summary={summary}
            loading={isSummaryLoading}
            onOpenDrill={openDrill}
          />

          <GeoSection
            pageFilters={filters}
            employeeStatus={employeeStatus}
            onApplyFilter={handleApplyFilter}
            onOpenDrill={openDrill}
          />

          <OrgSection
            pageFilters={filters}
            employeeStatus={employeeStatus}
            onApplyFilter={handleApplyFilter}
          />

          <CompositionSection
            pageFilters={filters}
            employeeStatus={employeeStatus}
            summary={summary}
            onApplyFilter={handleApplyFilter}
          />

          <LifecycleSnapshot summary={summary} />
        </Stack>
      </Box>

      <DrillPanel
        open={drillCtx != null}
        ctx={drillCtx}
        onClose={closeDrill}
        pageFilters={filters}
        employeeStatus={employeeStatus}
        summary={summary}
        onApplyFilter={handleApplyFilter}
      />
    </Box>
  );
};

export default HRStatsPage;
